import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Theme } from '@mui/system';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  SxProps,
  Tooltip,
  Typography
} from '@mui/material';
import { OverridableStringUnion } from '@mui/types';
import { FormControlPropsSizeOverrides } from '@mui/material/FormControl/FormControl';

import { InputOption } from 'models';
import { useAppSelector } from 'hooks/useAppStore';
import { selectAjaxLoader } from 'features/app/appSelectors';

interface FormControlSelectProps {
  name: string;
  value: any;
  onChange: (e: SelectChangeEvent) => any;
  onBlur?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => any;
  disabled?: boolean;
  captionLabel?: string;
  options?: InputOption[];
  errorMessage?: string;
  placeholder?: string;
  multiple?: boolean;
  size?: OverridableStringUnion<
    'small' | 'medium',
    FormControlPropsSizeOverrides
  >;
  formControlSx?: SxProps<Theme> | undefined;
  disableAjaxOperationInProgress?: boolean;
}

// ==============================|| FORM CONTROL SELECT ||============================== //

const FormControlSelect = ({
  name,
  value,
  captionLabel,
  options,
  errorMessage,
  onChange,
  onBlur,
  disabled,
  placeholder,
  multiple,
  size,
  formControlSx,
  disableAjaxOperationInProgress = false
}: FormControlSelectProps) => {
  const { t } = useTranslation();

  const operationInProgressSelector = useAppSelector(selectAjaxLoader);

  const operationInProgress = disableAjaxOperationInProgress
    ? false
    : operationInProgressSelector;

  const errorState = !!errorMessage?.length;

  const isEmpty = !options || options.length === 0;

  return (
    <FormControl fullWidth error={errorState} size={size} sx={formControlSx}>
      <InputLabel
        sx={{
          maxWidth: 'calc(100% - 40px)',
          '&.MuiInputLabel-shrink': { maxWidth: '100%' }
        }}
      >
        {t(captionLabel || '')}
      </InputLabel>
      <Select
        id={name}
        name={name}
        value={value}
        onChange={(e) => onChange(e as ChangeEvent<HTMLInputElement>)}
        onBlur={(e) => !!onBlur && onBlur(e)}
        label={t(captionLabel || '')}
        placeholder={placeholder}
        error={errorState}
        disabled={disabled ?? operationInProgress}
        multiple={multiple}
      >
        {isEmpty && (
          <MenuItem key='emptyItem' disabled>
            {t('common.noItems')}
          </MenuItem>
        )}
        {options?.map((option, index) =>
          option.helperText ? (
            <Tooltip key={index} title={t(option.helperText)}>
              <Typography component='span'>
                <MenuItem value={option.value as string} disabled>
                  {option.label}
                </MenuItem>
              </Typography>
            </Tooltip>
          ) : (
            <MenuItem
              key={index}
              value={option.value as string}
              disabled={
                disabled || option.value === '-1' || operationInProgress
              }
            >
              {option.label}
            </MenuItem>
          )
        )}
      </Select>
      {errorState && (
        <FormHelperText error id={`${name}-error`}>
          {t(errorMessage)}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default FormControlSelect;
